import {useDebounceValue} from '#/hooks/use-debounce-value';
import {fetchFromApiRoute} from '#/utils/fetch-utils';
import {useEffect, useState} from 'react';
import {ApiOpenGraph, OpenGraphLink, apiOpenGraph} from 'shareland-common';

export const useOpenGraphLinkPreview = (
  inputUrl: string,
  isInputUrlValid: boolean,
  debouncedDelay: number = 500,
): {
  data: OpenGraphLink | undefined;
  isLoading: boolean;
  error: string | undefined;
  reset: () => void;
} => {
  const debouncedInputUrl = useDebounceValue({
    value: inputUrl,
    ms: debouncedDelay,
    deps: [inputUrl],
  });
  const debouncedIsInputUrlValid = useDebounceValue({
    value: isInputUrlValid,
    ms: debouncedDelay,
    deps: [isInputUrlValid],
  });

  const [openGraphLink, setOpenGraphLink] = useState<OpenGraphLink>();
  const [isOpenGraphLinkLoading, setIsOpenGraphLinkLoading] = useState(false);
  const [openGraphLinkError, setOpenGraphLinkError] = useState<string | undefined>();

  const reset = () => {
    setOpenGraphLink(undefined);
    setIsOpenGraphLinkLoading(false);
    setOpenGraphLinkError(undefined);
  };

  useEffect(() => {
    const fetchOpenGraphLink = async () => {
      setIsOpenGraphLinkLoading(true);

      try {
        const res = await fetchFromApiRoute<ApiOpenGraph['getOrCreate']>(apiOpenGraph.endpoints.getOrCreate, {
          paramsDictionary: null,
          reqBody: null,
          reqQuery: {
            url: debouncedInputUrl,
          },
        });

        if (!!res) {
          setOpenGraphLink(res);
        } else {
          throw new Error();
        }
      } catch (error) {
        setOpenGraphLinkError('An error occured while fetching the link preview.');
      }

      setIsOpenGraphLinkLoading(false);
    };

    setOpenGraphLinkError(undefined);

    if (!debouncedInputUrl) {
      reset();
      return;
    }

    if (debouncedIsInputUrlValid) {
      fetchOpenGraphLink();
    }
  }, [debouncedInputUrl, debouncedIsInputUrlValid]);

  return {
    data: openGraphLink,
    isLoading: isOpenGraphLinkLoading,
    error: openGraphLinkError,
    reset,
  };
};
